import Backbone from 'backbone';
import { Model as UserModel } from 'models/user';
import { isPresentationMode } from 'env';
import { trackLoadedApplication } from 'tracking/events/core';
import { initializeUserTracking } from 'tracking/tracking';
import { PromiseQueue, queuePromises } from 'utils/PromiseQueue';
import { logError } from '@ardoq/logging';
import { getApiUrl } from 'backboneExtensions';
import { BasicModel, CurrentUserBackboneModel } from 'aqTypes';
import { userOperations } from '@ardoq/core';

type CurrentUserClass = CurrentUserBackboneModel & {
  new (): CurrentUserBackboneModel;
};

const Model: CurrentUserClass = UserModel.extend({
  idAttribute: '_id',
  url: `${getApiUrl(Backbone.Model)}/api/user/current_user`,
  loaded: false,
  initialize: function (this: CurrentUserBackboneModel) {
    if (isPresentationMode()) {
      this.set('public', true);
      this.loaded = true;
      this.trigger('sync', this);
      this.trigger('loaded');
      return;
    }
    this.startLoggedInSession();
  },
  /**
   * Starts user tracking on the next sync and queues saves. Call it once,
   * before the logged-in user's data is set on the model.
   */
  startLoggedInSession: function (this: CurrentUserBackboneModel) {
    this.once('sync', () => {
      // Attributes might be updated via the User object - and it does not have organizations
      // list which is only available from current_user services
      // Cache organizations list.
      this.organization = this.attributes.organization;
      this.organizations = this.attributes.organizations;
      initializeUserTracking(this.attributes, this.getOrganization());
      trackLoadedApplication();
    });

    /*
     * Saving a user might happen very frequently.
     * Thus, we must chain user saves to ensure that we don't
     * update the user with an incorrect version
     * (this can happen when a new version is made because of a previous save)
     */
    const saveQueue = new PromiseQueue();
    this.save = queuePromises(this.save.bind(this), saveQueue);
  },
  /**
   * Since the url of this model is /current_user
   * we must save it by delegating to UserModel (which puts to the correct endpoint)
   */
  save: function (attributes: Record<string, any>, options = {}) {
    return UserModel.prototype.save
      .call(this, attributes, {
        ...options,
        url: `${getApiUrl(Backbone.Model)}/api/user/${this.id}`,
      })
      .catch((error: unknown) => {
        logError(error as Error, 'Failed to save user');
      });
  },
  getOrganization: function (this: CurrentUserBackboneModel) {
    return (
      this.organization || {
        id: '',
        name: 'undefined-org',
        label: 'Undefined Organization',
      }
    );
  },
  isTrial: function (this: CurrentUserBackboneModel) {
    return this.organization && this.organization['is-trial'];
  },
  isArdoqEmployee: function (this: CurrentUserBackboneModel) {
    return /.+@ardoq.com$/.test(this.get('email') || '');
  },
  isOrgAdmin: function (this: CurrentUserBackboneModel): boolean {
    return userOperations.hasAdminRole(this.attributes);
  },
  /**
   * Sets a personal setting locally without saving it. Only for a user who is
   * not loaded, such as an anonymous viewer of a public presentation.
   */
  setLocalPersonalSetting: function (
    this: CurrentUserBackboneModel,
    key: string,
    val: unknown
  ) {
    this.set('settings', { ...this.get('settings'), [key]: val });
  },
  getPersonalSetting: function (this: CurrentUserBackboneModel, key: string) {
    return (this.get('settings') || {})[key];
  },
  /**
   * @param {BasicModel} obj
   * @this {CurrentUserBackboneModel}
   */
  canUnlock: function (this: CurrentUserBackboneModel, obj: BasicModel) {
    return this.id === obj.getLock() || this.isOrgAdmin();
  },
});

export const currentUser = new Model();
